import { useState, useCallback } from 'react';
import type { Integration, CreateIntegrationDto, UpdateIntegrationDto, ProviderConfig } from '@yawi/types';
import { Wizard, type WizardStep } from '@/core/ui/components';
import { TypeStep, ProviderStep, ConfigStep, SummaryStep } from './steps';
import { initialWizardData, type IntegrationWizardData } from './types';
import { providersByType } from './providerConfig';

const STEPS: WizardStep[] = [
  { id: 'type', label: 'Tipo' },
  { id: 'provider', label: 'Proveedor' },
  { id: 'config', label: 'Configuración' },
  { id: 'summary', label: 'Resumen' },
];

interface IntegrationWizardProps {
  integration?: Integration | null;
  isSaving: boolean;
  onSave: (data: CreateIntegrationDto | UpdateIntegrationDto, isNew: boolean) => Promise<void>;
  onClose: () => void;
}

export function IntegrationWizard({
  integration,
  isSaving,
  onSave,
  onClose,
}: IntegrationWizardProps) {
  const isEditing = !!integration;

  // Initialize data from existing integration or defaults
  const getInitialData = (): IntegrationWizardData => {
    if (integration) {
      return {
        type: integration.type,
        provider: integration.provider,
        name: integration.name,
        priority: integration.priority,
        config: integration.config as unknown as Record<string, string | number | boolean>,
      };
    }
    return initialWizardData;
  };

  const [currentStep, setCurrentStep] = useState(isEditing ? 2 : 0); // Skip to config for edit
  const [data, setData] = useState<IntegrationWizardData>(getInitialData);
  const [stepValidity, setStepValidity] = useState<boolean[]>([
    true, // Type always valid (has default)
    true, // Provider always valid (has default)
    false, // Config needs validation
    true, // Summary always valid
  ]);

  const handleDataChange = useCallback((newData: Partial<IntegrationWizardData>) => {
    setData((prev) => ({ ...prev, ...newData }));
  }, []);

  const handleValidChange = useCallback((isValid: boolean) => {
    setStepValidity((prev) => {
      const newValidity = [...prev];
      newValidity[currentStep] = isValid;
      return newValidity;
    });
  }, [currentStep]);

  const handleStepClick = useCallback((step: number) => {
    // For editing, don't allow going back to type/provider
    if (isEditing && step < 2) return;
    setCurrentStep(step);
  }, [isEditing]);

  const handlePrev = useCallback(() => {
    // For editing, don't go back past config step
    const minStep = isEditing ? 2 : 0;
    setCurrentStep((s) => Math.max(minStep, s - 1));
  }, [isEditing]);

  const handleNext = useCallback(() => {
    if (stepValidity[currentStep]) {
      setCurrentStep((s) => Math.min(STEPS.length - 1, s + 1));
    }
  }, [currentStep, stepValidity]);

  const handleSubmit = async () => {
    if (isEditing) {
      const updateData: UpdateIntegrationDto = {
        name: data.name,
        priority: data.priority,
        config: data.config as unknown as Partial<ProviderConfig>,
      };
      await onSave(updateData, false);
    } else {
      const createData: CreateIntegrationDto = {
        type: data.type,
        provider: data.provider,
        name: data.name,
        priority: data.priority,
        config: data.config as unknown as ProviderConfig,
      };
      await onSave(createData, true);
    }
  };

  const renderStep = () => {
    const stepProps = {
      data,
      onChange: handleDataChange,
      onValidChange: handleValidChange,
    };

    switch (currentStep) {
      case 0:
        return <TypeStep {...stepProps} />;
      case 1:
        return <ProviderStep {...stepProps} />;
      case 2:
        return <ConfigStep {...stepProps} />;
      case 3:
        return <SummaryStep {...stepProps} />;
      default:
        return null;
    }
  };

  // For editing, show only config and summary steps
  const visibleSteps = isEditing ? STEPS.slice(2) : STEPS;
  const displayStep = isEditing ? currentStep - 2 : currentStep;

  return (
    <Wizard
      title={isEditing ? 'Editar Integración' : 'Nueva Integración'}
      subtitle={isEditing
        ? `Modificar ${data.name}`
        : 'Configura un nuevo proveedor de integración'
      }
      steps={visibleSteps}
      currentStep={displayStep}
      stepValidity={isEditing ? stepValidity.slice(2) : stepValidity}
      onStepClick={(step) => handleStepClick(isEditing ? step + 2 : step)}
      onPrev={handlePrev}
      onNext={handleNext}
      onSubmit={handleSubmit}
      onClose={onClose}
      canGoNext={stepValidity[currentStep]}
      isSubmitting={isSaving}
      submitLabel={isEditing ? 'Guardar Cambios' : 'Crear Integración'}
      allowStepNavigation={!isEditing}
    >
      {renderStep()}
    </Wizard>
  );
}
